import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Boxes } from 'lucide-react';
import { InventoryPage } from './InventoryChrome';
import BundlesTab from '../pos/hardware/BundlesTab';
import BundleBuilder from '../pos/hardware/BundleBuilder';
import { hardwareApi, type DeviceModelRow } from '../../services/hardwareApi';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * BUNDLE EDITOR — full page under /inventory.
 *
 * Moved out of the old `pos-config` "Bundles" tab (pos-config cleanup).
 * `BundlesTab` is a PROTECTED file (owned by another in-flight feature) —
 * this host only IMPORTS/MOUNTS it, unchanged, never edits it. The Inventory
 * Hub's bundle-building flows land on the list here.
 *
 * `/inventory/bundles-editor/new` IS THE BUILDER, ON ITS OWN, and
 * `/inventory/bundles-editor/<bundleId>` is the same builder on an existing
 * kit. Both exist because a bundle used to be creatable and then unreachable:
 * the Bundles list could archive one, and nothing anywhere could edit it.
 *
 * IT IS A PAGE, NOT A POPUP. The builder renders with `variant="page"` — no
 * overlay, no 92vh cap, nothing behind it — because building a kit is real
 * work with room to grow, and a dialog over a list left every bundle (archived
 * ones included) legible through the backdrop, reading as a page that failed
 * to navigate. Closing or saving returns to the bundles list at
 * `/inventory/bundles`, which is where the operator started.
 */
const BundlesEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { bundleId } = useParams<{ bundleId: string }>();
  const [searchParams] = useSearchParams();
  const buildingNew = pathname.endsWith('/new');
  const editing = !!bundleId && !buildingNew;
  const inBuilder = buildingNew || editing;
  /**
   * COPYING — `/inventory/bundles-editor/new?copyFrom=<bundleId>`. It is the
   * NEW route, not a route of its own, because that is exactly what it is: a
   * create whose form arrives pre-filled. The source kit is only read.
   */
  const copyFromId = buildingNew ? searchParams.get('copyFrom') : null;

  // The builder needs the catalog for its parts picker. Only fetched when the
  // builder is actually on screen — the list below loads its own.
  const [models, setModels] = useState<DeviceModelRow[] | null>(null);

  useEffect(() => {
    if (!inBuilder || models) return undefined;
    let cancelled = false;
    hardwareApi.listModels()
      // An empty catalog is a legitimate answer here; the builder says so
      // itself. A failed load is treated the same way rather than holding the
      // operator on a spinner that will never end.
      .then((r) => { if (!cancelled) setModels(r.items || []); })
      .catch(() => { if (!cancelled) setModels([]); });
    return () => { cancelled = true; };
  }, [inBuilder, models]);

  /**
   * BACK TO THE BUNDLES LIST — /inventory/bundles, which is where "+ New
   * bundle" and Edit are pressed. Closing used to land on the bundle-editor
   * list instead: a second list, one the operator never asked for and did not
   * come from.
   */
  const backToList = () => navigate('/inventory/bundles', { replace: true });

  if (inBuilder) {
    return (
      <InventoryPage
        wide
        icon={Boxes}
        title={editing ? 'Edit bundle' : copyFromId ? 'Copy bundle' : 'New bundle'}
        subtitle={copyFromId
          ? 'A new kit, pre-filled from an existing one — the original is not changed'
          : 'Build a named kit from catalog devices'}
      >
        {models === null ? (
          <RubyLoader variant="page" />
        ) : (
          <BundleBuilder
            variant="page"
            bundleId={editing ? bundleId! : null}
            copyFromId={copyFromId}
            models={models}
            onClose={backToList}
            onSaved={backToList}
          />
        )}
      </InventoryPage>
    );
  }

  return (
    <InventoryPage icon={Boxes} title="Bundle Editor" subtitle="Build named kits from catalog devices">
      <BundlesTab />
    </InventoryPage>
  );
};

export default BundlesEditorPage;
